/**
 * client/src/lib/theme.ts — day or night, decided by the operating system, once.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THERE IS NO THEME CONTROL, AND WHY THAT IS A PRODUCT DECISION RATHER THAN A GAP
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * Every sibling repo has a theme picker. This one must not have one in P1.
 *
 * §3.1: "Nobody is asked to know what 'session voice' means in order to start." The general form of
 * that rule is that the family path has exactly one control per screen, and the first screen has one
 * control on the whole page. A theme toggle in a corner is a second control on the door — a thing to
 * notice, wonder about, and possibly press instead of the button — bought for a preference the
 * operating system already knows and that a 68-year-old will never go looking for.
 *
 * When Karti wants the console dark on a bright morning, he changes it in macOS and both faces
 * follow. That is one control, and it is one he already knows how to use.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * WHY A CLASS AND NOT A MEDIA QUERY
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `ui/tokens.css` is LIFTED byte-identical from audiobook (which lifted it from books) and it is
 * light-first: `:root` is day, `.dark` is night. Re-expressing that as
 * `@media (prefers-color-scheme: dark)` would mean a second copy of forty measured values inside a
 * query, which is the "sharing the names without sharing the values" failure books.karti.ai keeps a
 * monument to, and it would break `diff` against the two sibling files — the one property that makes
 * a lifted file worth lifting.
 *
 * ⚠️ **`apply()` must run before React renders, from `main.tsx`, and not from an effect.** A class
 * written in `useEffect` lands after the first paint, so a night-mode laptop opening the door screen
 * gets one frame of a cream page at full brightness. On this product that frame is the first thing
 * anybody ever sees of it.
 */

const DARK_CLASS = "dark";
const QUERY = "(prefers-color-scheme: dark)";

function apply(dark: boolean): void {
  document.documentElement.classList.toggle(DARK_CLASS, dark);
}

/**
 * Write the class now, and keep writing it when the OS changes.
 *
 * The listener is not a nicety: macOS switches appearance on a schedule, and a session is fifteen
 * minutes long in a living room in the evening. Turning the page from cream to ink *underneath* the
 * person recording, with no listener to do it deliberately, would instead mean the page stays cream
 * while every other window on her desktop goes dark — which reads as the page being stuck.
 *
 * Returns a teardown, so `main.tsx` could unhook it. Nothing calls the teardown today, and that is
 * correct rather than an oversight: this listener lives exactly as long as the document does.
 */
export function startTheme(): () => void {
  // `matchMedia` exists everywhere this ships, but a browser that answers `undefined` should get the
  // light theme rather than a thrown error at the very top of `main.tsx`, before the error boundary
  // that would have caught it has been mounted.
  const mq = typeof window.matchMedia === "function" ? window.matchMedia(QUERY) : null;
  apply(mq?.matches ?? false);
  if (!mq) return () => {};

  const onChange = (e: MediaQueryListEvent): void => apply(e.matches);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
